import { selectedFeatureAtom } from '@/atoms/selection.atom';
import {
  addPolygonModeAtom,
  DEFAULT_ADD_POLYGON_MODE,
  type PolygonPart,
} from '@/atoms/viewer.atom';
import { project } from '@/core';
import { execute } from '@/graphql/execute';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { formatArea, formatLength, ringArea2D, ringPerimeter } from '@/lib/geometryMeasure';
import { isRingSimple2D } from '@/lib/ringSimple';
import { createFeatureMutation, updateFeatureMutation } from '@/queries/feature.query';
import {
  Check,
  CheckSquare,
  Plus,
  Trash,
  Undo,
} from '@mynaui/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const isTypingTarget = (e: KeyboardEvent) => {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return (
    t.tagName === 'INPUT' ||
    t.tagName === 'TEXTAREA' ||
    t.tagName === 'SELECT' ||
    t.isContentEditable
  );
};

import {
  FloatingToolbar,
  FloatingToolbarButton,
  FloatingToolbarSeparator,
} from './FloatingToolbar';

interface AddPolygonToolbarProps {
  srid: number;
}

const POLYGON_FEATURE_TYPE = 'polygon';
const MIN_VERTICES_PER_RING = 3;

const xyOf = (verts: Array<[number, number, number]>): Array<[number, number]> =>
  verts.map(([x, y]) => [x, y]);

/** Append the closing duplicate vertex so the persisted ring is GeoJSON-valid. */
const closeRing = (ring: PolygonPart): PolygonPart =>
  ring.length === 0 ? ring : [...ring, ring[0]];

export const AddPolygonToolbar = ({ srid }: AddPolygonToolbarProps) => {
  const [mode, setMode] = useAtom(addPolygonModeAtom);
  const selectedFeature = useAtomValue(selectedFeatureAtom);
  const setSelectedFeature = useSetAtom(selectedFeatureAtom);
  const queryClient = useQueryClient();
  const { selectFeatureForEditing, createUpdateHandler } = useFeatureActions();
  const { t } = useTranslation('components');

  const isPicking = mode.phase === 'picking';
  const currentValid =
    mode.currentPart.length >= MIN_VERTICES_PER_RING &&
    isRingSimple2D(xyOf(mode.currentPart));
  const totalParts = mode.parts.length + (currentValid ? 1 : 0);
  const canFinalize = isPicking && (mode.parts.length > 0 || currentValid);
  // "+ Part" stays enabled when:
  //   • the current part is valid (≥3 simple verts → commits it and
  //     starts a new one), OR
  //   • there's at least one already-committed part and the current
  //     part is empty (the user just closed a ring via snap-to-first
  //     and wants visual confirmation that multi-part mode is alive).
  // The handler below treats the empty-current case as a no-op so
  // the button click doesn't change geometry — the user's next canvas
  // click is what actually starts picking the new part. Without this,
  // the button stayed disabled right after a ring-close and the user
  // mistook "no enabled button" for "no way to start a new part".
  const canAddPart =
    isPicking && (currentValid || (mode.parts.length > 0 && mode.currentPart.length === 0));
  const canUndo = isPicking && (mode.currentPart.length > 0 || mode.parts.length > 0);

  // Live area + perimeter. For closed parts, full ring area & perimeter.
  // For the in-progress current part, area is shoelace on whatever's been
  // picked so far (≥3 verts); perimeter sums the open polyline (not the
  // implicit closing edge — the user hasn't committed it yet).
  const totalAreaM2 =
    mode.parts.reduce((sum, p) => sum + ringArea2D(p), 0) +
    (mode.currentPart.length >= 3 ? ringArea2D(mode.currentPart) : 0);
  const totalPerimM =
    mode.parts.reduce((sum, p) => sum + ringPerimeter(p), 0) +
    // Open polyline length for the in-progress part.
    (mode.currentPart.length >= 2
      ? mode.currentPart.reduce((sum, v, i) => {
          if (i === 0) return 0;
          const a = mode.currentPart[i - 1];
          return (
            sum +
            Math.sqrt(
              (v[0] - a[0]) ** 2 + (v[1] - a[1]) ** 2 + (v[2] - a[2]) ** 2,
            )
          );
        }, 0)
      : 0);
  const measureLabel =
    totalAreaM2 > 0
      ? ` · ${formatArea(totalAreaM2)} · ${formatLength(totalPerimM)}`
      : totalPerimM > 0
        ? ` · ${formatLength(totalPerimM)}`
        : '';

  const status =
    (!isPicking
      ? t('addPolygonTool.adjusting')
      : mode.snapToFirst
        ? t('addPolygonTool.clickToCloseRing')
        : mode.currentPart.length === 0
          ? mode.parts.length === 0
            ? t('addPolygonTool.pickFirstPoint')
            : t('addPolygonTool.pickFirstPointOfPart', { partIndex: mode.parts.length + 1 })
          : mode.currentPart.length < MIN_VERTICES_PER_RING
            ? t('addPolygonTool.pickAtLeastN', {
                count: mode.currentPart.length,
                min: MIN_VERTICES_PER_RING,
              })
            : t('addPolygonTool.pickNextOrFinalize', {
                count: mode.currentPart.length,
                parts: totalParts,
              })) + measureLabel;

  const reset = () => setMode({ ...DEFAULT_ADD_POLYGON_MODE });

  const handleUndo = () => {
    if (!isPicking) return;
    if (mode.currentPart.length > 0) {
      setMode((prev) => ({
        ...prev,
        currentPart: prev.currentPart.slice(0, -1),
        snapToFirst: false,
      }));
      return;
    }
    if (mode.parts.length > 0) {
      // Restore the last committed ring as the current part (without its
      // closing duplicate) so the user can keep editing it.
      const last = mode.parts[mode.parts.length - 1];
      setMode((prev) => ({
        ...prev,
        parts: prev.parts.slice(0, -1),
        currentPart: last,
        snapToFirst: false,
      }));
    }
  };

  const handleAddPart = () => {
    if (!canAddPart) return;
    // Only commit when the current ring is actually valid; an empty
    // current ring means the user just closed via snap-to-first and
    // the button click is a confirming no-op.
    setMode((prev) => {
      if (
        prev.currentPart.length < MIN_VERTICES_PER_RING ||
        !isRingSimple2D(xyOf(prev.currentPart))
      ) {
        return { ...prev, snapToFirst: false };
      }
      return {
        ...prev,
        parts: [...prev.parts, prev.currentPart],
        currentPart: [],
        snapToFirst: false,
      };
    });
  };

  const buildGeometry = (allParts: PolygonPart[], targetSrid: number) => {
    const closedParts = allParts.map(closeRing);
    if (closedParts.length === 1) {
      return {
        type: 'Polygon' as const,
        crs: {
          type: 'name' as const,
          properties: { name: `urn:ogc:def:crs:EPSG::${targetSrid}` },
        },
        // Polygon coordinates: [[outerRing], [hole1], …]. v1 ships outer
        // rings only — holes are deferred per the plan.
        coordinates: [closedParts[0]],
      };
    }
    return {
      type: 'MultiPolygon' as const,
      crs: {
        type: 'name' as const,
        properties: { name: `urn:ogc:def:crs:EPSG::${targetSrid}` },
      },
      // MultiPolygon coordinates: each polygon is [[outerRing], [hole1], …].
      coordinates: closedParts.map((ring) => [ring]),
    };
  };

  const projectPartsToWGS84 = async (
    allParts: PolygonPart[],
  ): Promise<PolygonPart[] | null> => {
    if (!srid || srid === -1) return null;
    try {
      const out: PolygonPart[] = [];
      for (const part of allParts) {
        const projected = await Promise.all(part.map((c) => project(c, `EPSG:${srid}`)));
        if (projected.some((p) => !p)) return null;
        out.push(projected as PolygonPart);
      }
      return out;
    } catch {
      return null;
    }
  };

  const buildPolygonProperties = (existing: Record<string, any> = {}) => ({
    ...existing,
    featureType: POLYGON_FEATURE_TYPE,
    Name: existing.Name ?? `polygon ${Math.random().toString(36).slice(2, 8)}`,
  });

  const collectAllParts = (): PolygonPart[] => {
    if (currentValid) {
      return [...mode.parts, mode.currentPart];
    }
    return mode.parts;
  };

  const handleFinalize = () => {
    if (!canFinalize) return;
    const parts = collectAllParts();
    if (parts.length === 0) return;
    setMode((prev) => ({
      ...prev,
      parts,
      currentPart: [],
      snapToFirst: false,
      phase: 'adjusting',
    }));
  };

  const handleCancel = () => {
    if (mode.phase === 'adjusting' && selectedFeature?.isDraft) {
      setSelectedFeature(null);
    }
    reset();
  };

  const handleOk = async () => {
    const parts = mode.parts;
    if (parts.length === 0 || !mode.targetLayerId) return;
    const geometry = buildGeometry(parts, srid);
    const wgs84Parts = await projectPartsToWGS84(parts);
    const existing = mode.editFeatureId
      ? ((selectedFeature?.properties as Record<string, any> | undefined) ?? {})
      : {};
    const properties = buildPolygonProperties(existing);

    if (mode.editFeatureId) {
      const toastId = toast.loading(t('addPolygonTool.saving'));
      try {
        const input: any = { id: mode.editFeatureId, geometry, properties };
        if (wgs84Parts) input.geometryWGS84 = buildGeometry(wgs84Parts, 4326);
        const result = await execute(updateFeatureMutation, { input });
        const updated = result?.updateFeature;
        toast.success(t('addPolygonTool.saved'), { id: toastId });
        queryClient.invalidateQueries({ queryKey: ['features', mode.targetLayerId] });
        setSelectedFeature((prev) =>
          prev && updated
            ? {
                ...prev,
                geometry: updated.geometry,
                geometryWGS84: updated.geometryWGS84,
                properties: updated.properties,
              }
            : prev,
        );
        reset();
      } catch (e: any) {
        toast.error(e?.message || t('addPolygonTool.failedSave'), { id: toastId });
      }
      return;
    }

    const toastId = toast.loading(t('addPolygonTool.creating'));
    try {
      const input: any = { layerId: mode.targetLayerId, geometry, properties };
      if (wgs84Parts) input.geometryWGS84 = buildGeometry(wgs84Parts, 4326);
      const result = await execute(createFeatureMutation, { input });
      if (!result?.createFeature) throw new Error(t('addPolygonTool.failedCreate'));

      toast.success(t('addPolygonTool.created'), { id: toastId });
      queryClient.invalidateQueries({ queryKey: ['features', mode.targetLayerId] });

      const newFeature = result.createFeature;
      reset();
      selectFeatureForEditing({
        ...newFeature,
        layerId: mode.targetLayerId,
        onSave: createUpdateHandler(newFeature.id, mode.targetLayerId),
      });
    } catch (e: any) {
      toast.error(e?.message || t('addPolygonTool.failedCreate'), { id: toastId });
    }
  };

  const isAdjusting = mode.phase === 'adjusting';

  // Keyboard shortcuts — same set as AddLineToolbar.
  useEffect(() => {
    if (!mode.enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (isAdjusting) handleOk();
        else if (canFinalize) handleFinalize();
      } else if (
        e.key === 'Backspace' ||
        (e.key.toLowerCase() === 'z' && (e.ctrlKey || e.metaKey))
      ) {
        if (canUndo) {
          e.preventDefault();
          handleUndo();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedFeature, isAdjusting, canFinalize, canUndo]);

  if (!mode.enabled) return null;

  return (
    <FloatingToolbar status={status}>
      <FloatingToolbarButton
        icon={<Undo />}
        tooltip={t('addPolygonTool.undoPick')}
        disabled={!canUndo}
        onClick={handleUndo}
      />
      <FloatingToolbarButton
        icon={<Plus />}
        tooltip={t('addPolygonTool.addPart')}
        disabled={!canAddPart}
        onClick={handleAddPart}
      />
      <FloatingToolbarButton
        icon={<CheckSquare />}
        tooltip={t('addPolygonTool.finalize')}
        disabled={!canFinalize}
        onClick={handleFinalize}
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<Check />}
        tooltip={mode.editFeatureId ? t('addPolygonTool.save') : t('addPolygonTool.ok')}
        disabled={!isAdjusting}
        intent='primary'
        primaryClass='bg-purple-600 hover:bg-purple-700'
        onClick={handleOk}
      />
      <FloatingToolbarButton
        icon={<Trash />}
        tooltip={t('addPolygonTool.cancel')}
        intent='danger'
        onClick={handleCancel}
      />
    </FloatingToolbar>
  );
};
